import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * ReauthRequiredModal (issue #165)
 *
 * 自動保存 ON かつ Drive 構成済のユーザで token が失効しているとき、再生成 (投入) 前に
 * Drive 再ログインを促すモーダル。DiscardConfirmModal を範に createPortal + Esc + aria を踏襲する。
 * 「再ログインする」で onConfirm (consent 直行)、「キャンセル」で onCancel (閉じる)。
 */
interface ReauthRequiredModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ReauthRequiredModal: React.FC<ReauthRequiredModalProps> = ({ isOpen, onConfirm, onCancel }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-required-title"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadein"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-md animate-popin"
      >
        <h2 id="reauth-required-title" className="text-lg font-bold text-gray-800 mb-2">
          再ログインが必要です
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          自動保存が有効ですが、Google Drive のログインが切れています。
          再生成した PDF / 音源を Drive へ保存するため、もう一度ログインしてください。
        </p>
        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors"
          >
            再ログインする
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ReauthRequiredModal;
